import { useEffect, useRef } from 'react'
import type { NotificationEventEnvelope } from '../api/contracts'
import { subscribeToServerEvent } from '../api/serverEvents'

interface UseNotificationServerEventsOptions {
  enabled?: boolean
  onMessage: (event: NotificationEventEnvelope) => void
  /** Called when the stream is established again after a drop (not on the first connection). */
  onReconnect?: () => void
}

/** `notification_created` signals of the current user. The payload is a hint only: the feed row may not be committed yet. */
export function useNotificationServerEvents(
  apiBaseUrl: string,
  { enabled = true, onMessage, onReconnect }: UseNotificationServerEventsOptions
): void {
  const onMessageRef = useRef(onMessage)
  const onReconnectRef = useRef(onReconnect)

  useEffect(() => {
    onMessageRef.current = onMessage
    onReconnectRef.current = onReconnect
  }, [onMessage, onReconnect])

  useEffect(() => {
    if (!enabled) {
      return
    }

    // The shared connection reports itself to a subscriber that joins it already open, so the first call is always
    // the initial connection and every next one a reconnect.
    let connections = 0
    const subscription = subscribeToServerEvent(apiBaseUrl, {
      eventType: 'notification_created',
      onMessage: (event) => onMessageRef.current(event),
      onConnect: () => {
        connections += 1
        if (connections > 1) {
          onReconnectRef.current?.()
        }
      }
    })

    return () => {
      subscription.close()
    }
  }, [apiBaseUrl, enabled])
}
